export interface ReactNativeCodegenSignature {
  name: string;
  signature: string;
  isAsync: boolean;
}

export interface BrickCodegenSignature {
  name: string;
  signature: string;
  isAsync: boolean;
}

/**
 * React Native Codegen 시그니처를 Brick Codegen 시그니처로 변환
 */
export function mapReactNativeCodegenToBrickSignature(
  rnSignature: ReactNativeCodegenSignature,
): BrickCodegenSignature {
  let signature = rnSignature.signature;

  // 1. C++ struct 참조를 id로 변환
  signature = signature.replace(/JS::NativeBrickModule::\w+\s*&/g, "id");

  // 2. NSDictionary * 반환 타입을 id로 변환 (복잡한 객체의 경우)
  if (
    signature.includes("- (NSDictionary *)") &&
    isComplexObjectMethod(rnSignature.name)
  ) {
    signature = signature.replace("- (NSDictionary *)", "- (id)");
  }

  // 3. NSArray<NSString *> *를 NSArray *로 단순화
  signature = signature.replace(/NSArray<[^>]+>\s*\*/g, "NSArray *");

  // 4. 세미콜론 제거 (선택사항)
  signature = signature.replace(/;$/, "");

  return {
    name: rnSignature.name,
    signature: signature,
    isAsync: rnSignature.isAsync,
  };
}

/**
 * 메서드가 복잡한 객체를 다루는지 확인
 */
function isComplexObjectMethod(methodName: string): boolean {
  const complexObjectMethods = [
    "Calculator_oneDepth",
    "Calculator_twoDepth",
    "Calculator_threeDepth",
    "Calculator_processUser",
    "Calculator_processUsers",
  ];

  return complexObjectMethods.includes(methodName);
}

/**
 * TypeScript 타입을 Objective-C 타입으로 매핑 (React Native Codegen 호환)
 */
export function mapTypeScriptTypeToObjC(
  tsType: string,
  isReturnType: boolean = false,
  isAsync: boolean = false,
): string {
  // Promise 타입 처리
  if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
    return "void"; // async 함수는 void 반환
  }

  // 기본 타입 매핑
  switch (tsType) {
    case "string":
      return "NSString *";
    case "number":
      if (isReturnType && !isAsync) {
        return "NSNumber *"; // sync 함수의 number 반환은 NSNumber *
      }
      return "double"; // 파라미터나 async의 경우 double
    case "boolean":
      if (isReturnType && !isAsync) {
        return "NSNumber *"; // sync 함수의 boolean 반환은 NSNumber *
      }
      return "BOOL"; // 파라미터의 경우 BOOL
    case "void":
      return "void";
    case "any":
      return "id";
    default:
      // 배열 타입
      if (tsType.includes("[]")) {
        return "NSArray *";
      }
      // 객체 타입
      if (tsType.startsWith("{") && tsType.endsWith("}")) {
        return isReturnType ? "id" : "id"; // 복잡한 객체는 id
      }
      return "id";
  }
}

/**
 * 메서드 시그니처 생성
 */
export function generateMethodSignature(
  moduleName: string,
  methodName: string,
  parameters: Array<{ name: string; type: string }>,
  returnType: string,
  isAsync: boolean,
): string {
  const objcReturnType = mapTypeScriptTypeToObjC(returnType, true, isAsync);
  const fullMethodName = `${moduleName}_${methodName}`;

  let signature = `- (${objcReturnType})${fullMethodName}`;

  // 파라미터 추가
  if (parameters.length > 0) {
    const paramStrings = parameters.map((param, index) => {
      const objcType = mapTypeScriptTypeToObjC(param.type, false, isAsync);
      if (index === 0) {
        return `:(${objcType})${param.name}`;
      } else {
        return `${param.name}:(${objcType})${param.name}`;
      }
    });
    signature += paramStrings.join(" ");
  }

  // async 함수의 경우 resolve/reject 추가
  if (isAsync) {
    signature = signature.replace(`- (${objcReturnType})`, "- (void)");
    signature +=
      " resolve:(RCTPromiseResolveBlock)resolve reject:(RCTPromiseRejectBlock)reject";
  }

  return signature;
}

/**
 * React Native Codegen이 생성한 헤더 파일에서 private 접근 제어자를 제거
 */
export function removePrivateAccessModifiers(headerContent: string): string {
  // NSDictionary *_v; 앞의 private: 라인만 제거
  return headerContent.replace(
    /(\s*)private:\s*\n(\s*)NSDictionary \*_v;/g,
    "$1$2NSDictionary *_v;",
  );
}
